/**
 * chain-eos/mechat/inbox.ts — reading `mechatmechat` history from Hyperion.
 *
 * The contract has no tables: a conversation is reconstructed from action history.
 * We query every `mechatmechat::senddm` action the account appears in (both
 * directions come back in one call) and split them client-side.
 */

import { HYPERION_ENDPOINTS } from '../config.js'
import { MECHAT_ACTION, MECHAT_CONTRACT } from './constants.js'
import { decryptMessage, type EncryptedMessage } from './crypto.js'

export interface ChainMessage extends EncryptedMessage {
  trxId: string
  blockNum: number
  /** ISO timestamp from the block. */
  timestamp: string
  from: string
  to: string
  direction: 'in' | 'out'
}

export interface DecryptedChainMessage extends ChainMessage {
  /** Plaintext for incoming messages; null when unavailable (see `error`). */
  text: string | null
  /**
   * - `outgoing`     — sent by us, encrypted to the recipient: undecryptable by design
   * - `wrong-key`    — MAC mismatch: encrypted to another key of this account (rotated / multi-key)
   * - `malformed`    — payload could not be parsed
   */
  error?: 'outgoing' | 'wrong-key' | 'malformed'
}

export interface Conversation {
  /** The other account. */
  counterparty: string
  messages: DecryptedChainMessage[]
  lastMessageAt: string
  unreadHint: number
}

interface HyperionAction {
  trx_id: string
  block_num: number
  timestamp: string
  act: {
    account: string
    name: string
    data: {
      from: string
      to: string
      iv: string
      ephem_key: string
      cipher_text: string
      mac: string
    }
  }
}

export interface FetchMessagesOptions {
  account: string
  /** Max actions to pull (Hyperion caps a single call at 1000). */
  limit?: number
  /** Offset for paging further back. */
  skip?: number
  /** ISO timestamp — only actions at or after this point (incremental refresh). */
  after?: string
  signal?: AbortSignal
}

/**
 * Pulls raw (still encrypted) messages for `account`, newest first.
 * Tries each Hyperion endpoint in turn; throws only when all of them fail.
 */
export async function fetchMessages(options: FetchMessagesOptions): Promise<ChainMessage[]> {
  const account = String(options.account ?? '').trim()
  if (!account) return []

  const params = new URLSearchParams({
    account,
    filter: `${MECHAT_CONTRACT}:${MECHAT_ACTION}`,
    limit: String(Math.min(options.limit ?? 100, 1000)),
    sort: 'desc',
  })
  if (options.skip) params.set('skip', String(options.skip))
  if (options.after) params.set('after', options.after)

  let lastError: unknown
  for (const endpoint of HYPERION_ENDPOINTS) {
    try {
      const res = await fetch(`${endpoint}/v2/history/get_actions?${params.toString()}`, {
        signal: options.signal,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = (await res.json()) as { actions?: HyperionAction[] }
      return (body.actions ?? [])
        .filter(a => a.act?.account === MECHAT_CONTRACT && a.act?.name === MECHAT_ACTION)
        .map(a => ({
          trxId: a.trx_id,
          blockNum: a.block_num,
          timestamp: a.timestamp,
          from: a.act.data.from,
          to: a.act.data.to,
          direction: a.act.data.from === account ? ('out' as const) : ('in' as const),
          iv: a.act.data.iv,
          ephemPubKey: a.act.data.ephem_key,
          cipherText: a.act.data.cipher_text,
          mac: a.act.data.mac,
        }))
    } catch (err) {
      lastError = err
      console.warn(`[mechat] Hyperion get_actions failed on ${endpoint}:`, err)
    }
  }
  throw new Error(`[mechat] all Hyperion endpoints failed: ${String(lastError)}`)
}

/**
 * Decrypts every incoming message with the account's private key.
 * Outgoing messages are returned with `text: null, error: 'outgoing'` — merge the
 * device's local sent log over them in the UI layer.
 */
export async function decryptMessages(
  messages: ChainMessage[],
  privateKey: string,
): Promise<DecryptedChainMessage[]> {
  return Promise.all(
    messages.map(async (m): Promise<DecryptedChainMessage> => {
      if (m.direction === 'out') return { ...m, text: null, error: 'outgoing' }
      try {
        return { ...m, text: await decryptMessage(m, privateKey) }
      } catch (err) {
        const wrongKey = err instanceof Error && err.message === 'Invalid mac'
        return { ...m, text: null, error: wrongKey ? 'wrong-key' : 'malformed' }
      }
    }),
  )
}

/** Groups messages into conversations, newest conversation first. */
export function groupConversations(
  messages: DecryptedChainMessage[],
  account: string,
  options: { hiddenAccounts?: Iterable<string> } = {},
): Conversation[] {
  const hidden = new Set(options.hiddenAccounts ?? [])
  const byPeer = new Map<string, DecryptedChainMessage[]>()

  for (const m of messages) {
    const peer = m.from === account ? m.to : m.from
    if (hidden.has(peer)) continue
    const list = byPeer.get(peer)
    if (list) list.push(m)
    else byPeer.set(peer, [m])
  }

  return [...byPeer.entries()]
    .map(([counterparty, msgs]) => {
      const sorted = msgs.sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      return {
        counterparty,
        messages: sorted,
        lastMessageAt: sorted[sorted.length - 1]?.timestamp ?? '',
        unreadHint: sorted.filter(m => m.direction === 'in').length,
      }
    })
    .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
}
